/**
 * MCQ Set Player
 * Player component for MCQ Question Set content type
 */

import * as React from 'react';
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    Button,
    cn,
    Progress,
} from '@ai-content-hub/content-kit';
import { QuestionCounterLabel, useSinglePageNavPlacement } from '@ai-content-hub/content-core';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { MCQPlayer } from '../mcq/MCQPlayer';
import { isMcqAnswered } from '../mcq/grader';
import type { MCQAnswer } from '../mcq/schema';
import { BlockRenderer, isEmptyBlockContent } from '../block';
import {
    mcqSetSchema,
    type MCQSetData,
    type MCQSetItem,
    type MCQSetConfig,
    type MCQSetLayoutConfig,
    type MCQSetScoringConfig,
} from './schema';
import type { MCQSetResults } from './scoring';
import { computeMCQSetResults } from './scoring';
import { Check, RotateCcw } from 'lucide-react';
import './mcq-set-player.css';

function isMcqTabAnswered(answer: MCQAnswer | undefined): boolean {
    return isMcqAnswered(answer);
}

const JUMP_TO_CHILD_SCROLL_TOP_OFFSET_PX = 50;

function getMcqSetNavButtonProps(options: {
    answered: boolean;
}): { variant: 'default' | 'outline'; className: string } {
    const base =
        'mcq-set-player-tab mcq-set-player-single-page-nav-num cursor-pointer min-w-9 font-medium transition-colors duration-200';
    const { answered } = options;
    if (answered) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'mcq-set-player-tab--answered !border-success/60 !bg-success/15 text-foreground shadow-none hover:!bg-success/25 hover:text-foreground dark:!bg-success/15 dark:hover:!bg-success/25'
            ),
        };
    }
    return {
        variant: 'outline',
        className: cn(
            base,
            'mcq-set-player-tab--unseen !border-foreground/25 !bg-muted/80 text-foreground shadow-sm hover:!bg-muted hover:!border-foreground/40 dark:!bg-muted/80'
        ),
    };
}

function getMcqSetTabButtonProps(options: {
    isCurrent: boolean;
    answered: boolean;
    visited: boolean;
}): { variant: 'default' | 'outline'; className: string } {
    const { isCurrent, answered, visited } = options;
    const base = 'mcq-set-player-tab cursor-pointer min-w-9 transition-colors duration-200';
    if (isCurrent) {
        return {
            variant: 'default',
            className: cn(
                base,
                'mcq-set-player-tab--current ring-2 ring-primary/40 ring-offset-2 ring-offset-background'
            ),
        };
    }
    if (answered) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'mcq-set-player-tab--answered border-success/60 bg-success/15 text-foreground hover:bg-success/25 hover:text-foreground'
            ),
        };
    }
    if (visited) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'mcq-set-player-tab--visited-unanswered border-warning/60 bg-warning/15 text-foreground hover:bg-warning/25 hover:text-foreground'
            ),
        };
    }
    return {
        variant: 'outline',
        className: cn(
            base,
            'mcq-set-player-tab--unseen border-border text-muted-foreground hover:bg-muted/40'
        ),
    };
}

function getVerticalScrollParent(el: HTMLElement): HTMLElement | null {
    let parent: HTMLElement | null = el.parentElement;
    while (parent && parent !== document.documentElement) {
        if (parent === document.body) {
            parent = parent.parentElement;
            continue;
        }
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const canScrollY =
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
            parent.scrollHeight > parent.clientHeight + 1;
        if (canScrollY) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

function scrollElementToTopOffset(el: HTMLElement, offsetPx: number): void {
    const scrollParent = getVerticalScrollParent(el);
    if (scrollParent) {
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta = elRect.top - parentRect.top - offsetPx;
        scrollParent.scrollBy({ top: delta, behavior: 'smooth' });
        return;
    }
    const elRect = el.getBoundingClientRect();
    window.scrollBy({ top: elRect.top - offsetPx, behavior: 'smooth' });
}

export interface MCQSetPlayerProps {
    /** MCQ Set data - passed by content-core */
    data: MCQSetData;
    /** Optional config override - full ContentConfig from content-core */
    config?: MCQSetConfig & {
        behavior?: {
            allowRetry?: boolean;
            maxAttempts?: number;
            shuffleChoices?: boolean;
            showCorrectAnswer?: boolean;
            showExplanation?: boolean;
            instantFeedback?: boolean;
            examMode?: boolean;
            autoAdvance?: boolean;
        };
        scoring?: {
            max?: number;
            partialCreditEnabled?: boolean;
            passingScore?: number;
        };
        // EAA-1674: dropped ad-hoc inline theme shape (unread, no caller ever
        // passed it) — the player already sits inside ContentPlayer's
        // ThemeProvider (Phase 2), so theming flows through useContentTheme's
        // CSS vars + semantic tokens instead of a forwarded config.theme prop.
        i18n?: {
            defaultLanguage?: string;
        };
    };
    /** Called when an answer changes — receives full map { [itemId]: MCQAnswer } */
    onAnswer?: (answer: unknown) => void;
    /** Controlled answers from ContentPlayer */
    answer?: Record<string, MCQAnswer>;
    /** Grading output from ContentPlayer after submit */
    result?: {
        isCorrect?: boolean | null;
        score?: number;
        max?: number;
        details?: Record<string, unknown>;
    };
    /** Show feedback after submission */
    showFeedback?: boolean;
    /** In review mode */
    isReview?: boolean;
    /** Interaction disabled */
    disabled?: boolean;
    /** Additional class name */
    className?: string;
}

export const MCQSetPlayer: React.FC<MCQSetPlayerProps> = ({
    data,
    config,
    onAnswer,
    answer: answerProp,
    result: resultProp,
    showFeedback: showFeedbackProp,
    isReview = false,
    disabled = false,
    className,
}) => {
    const questionOffset = (config as { questionOffset?: number } | undefined)?.questionOffset ?? 0;
    const layoutConfig = config?.layout as MCQSetLayoutConfig | undefined;
    const scoringConfig = config?.scoring as MCQSetScoringConfig | undefined;
    const behaviorConfig = config?.behavior;
    const { t, language: activeLanguage } = useElementSchemaLocale(mcqSetSchema.i18n, {
        config,
    });

    const displayModeRaw = layoutConfig?.displayMode || 'all';
    const displayMode = displayModeRaw === 'step' ? 'tabs' : displayModeRaw;
    const showQuestionNumbers = layoutConfig?.showQuestionNumbers !== false;
    const answerChoicesPresentation = layoutConfig?.answerChoicesPresentation ?? 'standard';

    // Behavior settings from config
    const shuffleChoices = behaviorConfig?.shuffleChoices ?? false;
    const showCorrectAnswer = behaviorConfig?.showCorrectAnswer ?? true;
    const instantFeedback = behaviorConfig?.instantFeedback ?? false;
    const examMode = behaviorConfig?.examMode ?? false;
    const allowRetry = behaviorConfig?.allowRetry ?? true;
    const maxAttempts = behaviorConfig?.maxAttempts ?? 0; // 0 = unlimited

    // State - hooks must be called before any early returns
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [answers, setAnswers] = React.useState<Record<string, MCQAnswer>>(() =>
        answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)
            ? { ...answerProp }
            : {}
    );
    const [submitted, setSubmitted] = React.useState(isReview);
    const [localResults, setLocalResults] = React.useState<MCQSetResults | null>(null);
    const [attemptCount, setAttemptCount] = React.useState(0);
    const [visitedTabIndices, setVisitedTabIndices] = React.useState<Set<number>>(() => new Set());
    const questionAnchorRefs = React.useRef<Map<number, HTMLElement>>(new Map());
    const [flashIndex, setFlashIndex] = React.useState<number | null>(null);
    const flashClearRef = React.useRef<number | null>(null);

    const clearFlashTimeout = React.useCallback(() => {
        if (flashClearRef.current != null) {
            window.clearTimeout(flashClearRef.current);
            flashClearRef.current = null;
        }
    }, []);

    React.useEffect(() => () => clearFlashTimeout(), [clearFlashTimeout]);

    React.useEffect(() => {
        if (answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)) {
            setAnswers({ ...answerProp });
        }
    }, [answerProp]);

    React.useEffect(() => {
        if (!data?.items?.length) {
            setCurrentIndex(0);
            return;
        }
        setCurrentIndex((i) => Math.min(Math.max(0, i), data.items.length - 1));
    }, [data?.items?.length]);

    React.useEffect(() => {
        if (!data?.items?.length) {
            return;
        }
        const n = data.items.length;
        setVisitedTabIndices((prev) => {
            const next = new Set<number>();
            prev.forEach((i) => {
                if (i >= 0 && i < n) {
                    next.add(i);
                }
            });
            next.add(currentIndex);
            return next;
        });
    }, [currentIndex, data?.items?.length]);

    const resultsFromProp = React.useMemo(() => {
        const raw = resultProp?.details?.mcqSetResults as MCQSetResults | undefined;
        return raw ?? null;
    }, [resultProp]);

    const results = resultsFromProp ?? localResults;

    const submittedOrFeedback = Boolean(showFeedbackProp || isReview);
    const effectiveSubmitted = submitted || submittedOrFeedback;

    // Check if retry is allowed
    const canRetry = React.useMemo(() => {
        if (!allowRetry) return false;
        if (results?.passed) return false; // Don't allow retry if already passed
        if (maxAttempts === 0) return true; // Unlimited attempts
        return attemptCount < maxAttempts;
    }, [allowRetry, results?.passed, maxAttempts, attemptCount]);

    // Validate data - early return after all hooks
    if (!data || !data.items) {
        return (
            <div
                className={cn(
                    'mcq-set-player mcq-set-player--empty text-muted-foreground italic p-4',
                    className
                )}
            >
                {t('noQuestions', 'No questions available')}
            </div>
        );
    }

    // Handle answer
    const handleAnswer = (itemId: string, answer: MCQAnswer) => {
        if (disabled || effectiveSubmitted) return;
        setAnswers((prev) => {
            const next = { ...prev, [itemId]: answer };
            onAnswer?.(next);
            return next;
        });
    };

    const calculateResults = (): MCQSetResults => {
        const mergedPlayerScoring = {
            ...config?.scoring,
            ...scoringConfig,
        };
        return computeMCQSetResults(data, answers, mergedPlayerScoring, config);
    };

    const handleSubmit = () => {
        const nextResults = calculateResults();
        setLocalResults(nextResults);
        setSubmitted(true);
        setAttemptCount((prev) => prev + 1);
    };

    const handleRetry = () => {
        if (!canRetry) return;
        setAnswers({});
        setSubmitted(false);
        setLocalResults(null);
        setCurrentIndex(0);
        setVisitedTabIndices(new Set([0]));
    };

    // Check if all questions answered
    const allAnswered = data.items.every((item) => isMcqTabAnswered(answers[item.id]));

    const attachQuestionAnchor = React.useCallback(
        (index: number, el: HTMLElement | null) => {
            if (displayMode !== 'all') {
                if (el == null) {
                    questionAnchorRefs.current.delete(index);
                }
                return;
            }
            if (el != null) {
                questionAnchorRefs.current.set(index, el);
            } else {
                questionAnchorRefs.current.delete(index);
            }
        },
        [displayMode]
    );

    const handleJumpToQuestion = React.useCallback(
        (index: number) => {
            if (index < 0 || index >= (data?.items?.length ?? 0)) return;
            const el = questionAnchorRefs.current.get(index);
            if (el) {
                scrollElementToTopOffset(el, JUMP_TO_CHILD_SCROLL_TOP_OFFSET_PX);
            }
            clearFlashTimeout();
            setFlashIndex(index);
            flashClearRef.current = window.setTimeout(() => {
                setFlashIndex(null);
                flashClearRef.current = null;
            }, 1300);
        },
        [data?.items?.length, clearFlashTimeout]
    );

    // Single-page question navigation bar for "all" display mode
    const showSinglePageNav = displayMode === 'all' && data.items.length > 1;

    const singlePageNavEl = React.useMemo(() => {
        if (!showSinglePageNav) return null;
        return (
            <div
                className="mcq-set-player-single-page-nav flex flex-wrap items-center justify-center gap-2"
                role="navigation"
                aria-label={t('questionNavigation', 'Question navigation')}
            >
                <div className="mcq-set-player-single-page-nav-numbers flex flex-wrap justify-center gap-2">
                    {data.items.map((item, idx) => {
                        const navProps = getMcqSetNavButtonProps({
                            answered: isMcqTabAnswered(answers[item.id]),
                        });
                        return (
                            <Button
                                key={item.id}
                                type="button"
                                size="sm"
                                variant={navProps.variant}
                                className={cn(
                                    navProps.className,
                                    'mcq-set-player-single-page-nav-btn'
                                )}
                                onClick={() => handleJumpToQuestion(idx)}
                                disabled={disabled}
                            >
                                {questionOffset + idx + 1}
                            </Button>
                        );
                    })}
                </div>
            </div>
        );
    }, [showSinglePageNav, data.items, answers, disabled, handleJumpToQuestion, questionOffset, t]);

    const { inlineNav: inlineSinglePageNav } = useSinglePageNavPlacement(singlePageNavEl, {
        inlineSinglePageNav: true,
        revision: JSON.stringify(answers),
    });

    // Render question
    const renderQuestion = (item: MCQSetItem, index: number) => {
        const answer = answers[item.id];
        const itemResult = results?.items.find((r) => r.itemId === item.id);

        // Merge item data with behavior config (config overrides data)
        const mergedItemData = {
            ...item.data,
            shuffleChoices: shuffleChoices || item.data.shuffleChoices,
        };

        // Determine when to show feedback based on behavior settings
        const shouldShowFeedback = examMode
            ? effectiveSubmitted
            : instantFeedback
              ? answer !== undefined
              : effectiveSubmitted;

        const showCounter = data.items.length > 1;

        return (
            <div
                key={item.id}
                ref={(el) => attachQuestionAnchor(index, el)}
                className={cn(
                    'mcq-set-player-question-item scroll-mt-6 rounded-md',
                    displayMode === 'all' && flashIndex === index && 'mcq-set-player-question-item--flash'
                )}
            >
                <div className={cn('mcq-set-player-question-inner', showCounter && 'flex gap-3 items-start py-2')}>
                    {showCounter && <QuestionCounterLabel index={questionOffset + index} className="mt-1" />}
                    <div className="min-w-0 flex-1">
                        <MCQPlayer
                            data={mergedItemData}
                            answer={answer}
                            onAnswer={(ans) => handleAnswer(item.id, ans as MCQAnswer)}
                            answerChoicesPresentation={answerChoicesPresentation}
                            config={config}
                            language={activeLanguage}
                            disabled={disabled || effectiveSubmitted}
                            showFeedback={shouldShowFeedback && showCorrectAnswer}
                            result={
                                itemResult
                                    ? {
                                          isCorrect: itemResult.isCorrect,
                                          score: itemResult.score,
                                          max: itemResult.max,
                                      }
                                    : undefined
                            }
                            isReview={effectiveSubmitted}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // Render results summary
    const renderResults = () => {
        if (!results) return null;

        return (
            <Card
                className={cn(
                    'mcq-set-player-results mt-6',
                    // EAA-1674: raw green/red palette classes → success/destructive
                    // semantic tokens so results theming follows global design tokens.
                    results.passed ? 'border-success' : 'border-destructive'
                )}
            >
                <CardHeader className="mcq-set-player-results-header">
                    <CardTitle
                        className={cn(
                            'mcq-set-player-results-title',
                            results.passed ? 'text-success' : 'text-destructive'
                        )}
                    >
                        {results.passed
                            ? t('congratulations', '🎉 Congratulations!')
                            : t('keepPracticing', '📚 Keep Practicing')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="mcq-set-player-results-body">
                    <div className="mcq-set-player-results-score space-y-4">
                        <div className="text-4xl font-bold text-center">
                            {Math.round(results.percentage * 100)}%
                        </div>
                        <div className="text-center text-muted-foreground">
                            {t('score', 'Score')}: {results.totalScore} / {results.max}
                        </div>
                        <Progress
                            value={results.percentage * 100}
                            className={results.passed ? 'bg-success/15' : 'bg-destructive/15'}
                        />
                        {/* Retry button */}
                        {canRetry && (
                            <div className="mcq-set-player-results-retry text-center pt-4">
                                <Button variant="outline" size="lg" onClick={handleRetry}>
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    {t('retry', 'Try Again')}
                                    {maxAttempts > 0 && (
                                        <span className="ml-2 text-muted-foreground">
                                            ({maxAttempts - attemptCount}{' '}
                                            {t('attemptsLeft', 'attempts left')})
                                        </span>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <div className={cn('mcq-set-player space-y-6', className)}>
            {/* Header */}
            {data.description && !isEmptyBlockContent(data.description) && (
                <div className="mcq-set-player-header text-center">
                    <div className="mcq-set-player-description mt-1">
                        <BlockRenderer content={data.description} />
                    </div>
                </div>
            )}

            {/* Progress */}
            {/* {showProgress && !submitted && (
                <div className="mcq-set-player-progress space-y-2">
                    <div className="mcq-set-player-progress-header flex justify-between text-sm text-muted-foreground">
                        <span>{t('progress', 'Progress')}</span>
                        <span>
                            {answeredCount} / {data.items.length} {t('answered', 'answered')}
                        </span>
                    </div>
                    <Progress value={(answeredCount / data.items.length) * 100} />
                </div>
            )} */}

            {/* Questions */}
            {displayMode === 'all' ? (
                // All questions at once
                <>
                    <div className="mcq-set-player-questions space-y-6">
                        {data.items.map((item, index) => renderQuestion(item, index))}
                    </div>
                    {inlineSinglePageNav}
                </>
            ) : (
                <div className="mcq-set-player-tabs-mode flex flex-col gap-6 min-h-0">
                    {effectiveSubmitted ? (
                        <div className="mcq-set-player-questions mcq-set-player-questions--tabs-review space-y-6">
                            {data.items.map((item, index) => renderQuestion(item, index))}
                        </div>
                    ) : (
                        <>
                            {data.items[currentIndex]
                                ? renderQuestion(data.items[currentIndex], currentIndex)
                                : null}
                            {data.items.length > 1 ? (
                                <div
                                    className="mcq-set-player-tab-row mcq-set-player-tab-row--bottom flex flex-wrap justify-center gap-2 border-t border-border pt-4"
                                    role="tablist"
                                >
                                    {data.items.map((item, idx) => {
                                        const tabProps = getMcqSetTabButtonProps({
                                            isCurrent: idx === currentIndex,
                                            answered: isMcqTabAnswered(answers[item.id]),
                                            visited: visitedTabIndices.has(idx),
                                        });
                                        return (
                                            <Button
                                                key={item.id}
                                                type="button"
                                                size="sm"
                                                variant={tabProps.variant}
                                                className={tabProps.className}
                                                aria-selected={idx === currentIndex}
                                                onClick={() => setCurrentIndex(idx)}
                                                disabled={disabled}
                                            >
                                                {questionOffset + idx + 1}
                                            </Button>
                                        );
                                    })}
                                </div>
                            ) : null}
                            {allAnswered && data.items.length > 0 ? (
                                <div className="mcq-set-player-tabs-submit flex justify-end pb-1">
                                    <Button
                                        type="button"
                                        className="cursor-pointer"
                                        onClick={handleSubmit}
                                        disabled={!allAnswered}
                                    >
                                        <Check className="h-4 w-4 mr-2" />
                                        {t('submit', 'Submit')}
                                    </Button>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            )}

            {/* Results — hide when ContentPlayer already shows ScoreSummary (graded via engine) */}
            {effectiveSubmitted && resultsFromProp == null && showCorrectAnswer && renderResults()}
        </div>
    );
};

export default MCQSetPlayer;
